import React, { useMemo } from 'react';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useData } from '../../context/DataContext';
import { usePerfil } from '../../context/PerfilContext';
import { formatoEuros, formatoPorcentaje } from '../../utils/numeros';
import { periodoAnio, periodoMes, porCuenta, progresoHuchas, seriePatrimonio } from '../../utils/informes';
import {
  BloquePlus, EtiquetaPlus, mesCorto, TablaDatos, Tarjeta, TooltipEuros, Vacio, VistaInforme, ejeEuros, useColoresGrafico,
} from './comun';

const nombreMes = (anio: number, mes: number) =>
  new Date(anio, mes - 1, 1).toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });

/** I4: en qué cuenta o tarjeta se va el dinero. */
const GastoPorCuenta: React.FC<{ vista: VistaInforme }> = ({ vista }) => {
  const { transacciones, cuentas } = useData();
  const filas = useMemo(
    () => porCuenta(transacciones, cuentas, vista.mes === null ? periodoAnio(vista.anio) : periodoMes(vista.anio, vista.mes)),
    [transacciones, cuentas, vista.anio, vista.mes],
  );
  const max = Math.max(...filas.map(f => Math.max(f.gastos, f.ingresos)), 1);
  const totalGastos = filas.reduce((s, f) => s + f.gastos, 0);

  return (
    <Tarjeta titulo="Por cuenta y tarjeta" subtitulo="Gastos e ingresos de cada una en el periodo (sin traspasos ni adelantos).">
      {filas.length === 0 ? <Vacio>No hay movimientos en este periodo.</Vacio> : (
        <ul className="space-y-3">
          {filas.map(f => (
            <li key={f.cuenta.id}>
              <div className="flex items-center gap-2 text-sm">
                <span className="shrink-0">{f.cuenta.icono ?? '🏦'}</span>
                <span className="flex-1 min-w-0 truncate font-medium text-gray-900 dark:text-white">{f.cuenta.nombre}</span>
                <span className="tabular-nums text-red-600 dark:text-red-400 font-semibold">{formatoEuros(f.gastos)}</span>
                {totalGastos > 0 && <span className="text-xs text-gray-400 w-10 text-right tabular-nums">{formatoPorcentaje((f.gastos / totalGastos) * 100)}</span>}
              </div>
              <div className="pl-7 mt-1 space-y-1">
                <div className="bg-gray-100 dark:bg-gray-700 rounded-full h-1.5" title={`Gastos: ${formatoEuros(f.gastos)}`}>
                  <div className="h-1.5 rounded-full" style={{ width: `${(f.gastos / max) * 100}%`, backgroundColor: '#C8584A' }} />
                </div>
                {f.ingresos > 0 && (
                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-gray-100 dark:bg-gray-700 rounded-full h-1.5" title={`Ingresos: ${formatoEuros(f.ingresos)}`}>
                      <div className="h-1.5 rounded-full" style={{ width: `${(f.ingresos / max) * 100}%`, backgroundColor: '#6584A0' }} />
                    </div>
                    <span className="text-xs text-gray-500 dark:text-gray-400 tabular-nums">+{formatoEuros(f.ingresos)}</span>
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Tarjeta>
  );
};

/** I6: cuánto falta en cada hucha y cuándo se llegaría al ritmo de ahora. */
const Huchas: React.FC = () => {
  const { transacciones, cuentas } = useData();
  const huchas = useMemo(() => progresoHuchas(cuentas, transacciones), [cuentas, transacciones]);
  if (huchas.length === 0) return null;

  return (
    <Tarjeta titulo="Huchas" subtitulo="La fecha sale de lo que ha entrado de media en los últimos 3 meses.">
      <ul className="space-y-4">
        {huchas.map(h => (
          <li key={h.cuenta.id}>
            <div className="flex items-center gap-2 text-sm">
              <span className="shrink-0">{h.cuenta.icono ?? '🐷'}</span>
              <span className="flex-1 min-w-0 truncate font-medium text-gray-900 dark:text-white">{h.cuenta.nombre}</span>
              <span className="tabular-nums font-semibold text-gray-900 dark:text-white">{formatoEuros(h.cuenta.saldo)}</span>
              <span className="text-xs text-gray-400 tabular-nums">de {formatoEuros(h.objetivo)}</span>
            </div>
            <div className="pl-7 mt-1.5">
              <div className="bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                <div className="h-2 rounded-full bg-green-500" style={{ width: `${Math.max(h.porcentaje, 0)}%` }} />
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {h.falta <= 0
                  ? '¡Objetivo conseguido!'
                  : h.fechaEstimada
                    ? <>Faltan {formatoEuros(h.falta)}. Metiendo {formatoEuros(h.ritmoMensual)} al mes, lo tendrás en <span className="font-semibold">{h.fechaEstimada.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })}</span>.</>
                    : <>Faltan {formatoEuros(h.falta)}. En los últimos 3 meses no ha entrado dinero, así que no hay fecha estimada.</>}
              </p>
            </div>
          </li>
        ))}
      </ul>
    </Tarjeta>
  );
};

/** I7: patrimonio neto (cuentas, huchas e inversiones) a fin de cada mes. */
const Patrimonio: React.FC<{ anioFin: number; mesFin: number }> = ({ anioFin, mesFin }) => {
  const { transacciones, cuentas, inversiones, aportacionesInversion, retirosInversion, valoracionesInversion } = useData();
  const c = useColoresGrafico();
  const serie = useMemo(() => seriePatrimonio(
    cuentas, transacciones,
    { inversiones, aportaciones: aportacionesInversion, retiros: retirosInversion, valoraciones: valoracionesInversion },
    anioFin, mesFin, 12,
  ), [cuentas, transacciones, inversiones, aportacionesInversion, retirosInversion, valoracionesInversion, anioFin, mesFin]);
  const ultimo = serie[serie.length - 1];
  const primero = serie[0];
  const cambio = ultimo.total - primero.total;

  return (
    <Tarjeta titulo="Patrimonio" subtitulo={`Lo que tienes a fin de cada mes, hasta ${nombreMes(anioFin, mesFin)}`} accion={<EtiquetaPlus />}>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
        {[
          { t: 'Total', v: ultimo.total, fuerte: true },
          { t: 'Cuentas', v: ultimo.cuentas },
          { t: 'Huchas', v: ultimo.huchas },
          { t: 'Inversiones', v: ultimo.inversiones },
        ].map(x => (
          <div key={x.t} className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-2">
            <p className="text-xs text-gray-500 dark:text-gray-400">{x.t}</p>
            <p className={`tabular-nums ${x.fuerte ? 'font-bold text-gray-900 dark:text-white' : 'font-semibold text-gray-700 dark:text-gray-200'}`}>{formatoEuros(x.v)}</p>
          </div>
        ))}
      </div>
      <div className="h-48">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={serie} margin={{ top: 8, right: 4, left: 0, bottom: 0 }}>
            <CartesianGrid vertical={false} stroke={c.rejilla} />
            <XAxis dataKey="etiqueta" tick={{ fontSize: 11, fill: c.suave }} tickLine={false} axisLine={{ stroke: c.rejilla }} />
            <YAxis tick={{ fontSize: 11, fill: c.suave }} tickLine={false} axisLine={false} tickFormatter={ejeEuros} width={56} domain={['auto', 'auto']} />
            <Tooltip content={<TooltipEuros titulo={l => {
              const p = serie.find(x => x.etiqueta === l);
              return p ? `Fin de ${nombreMes(p.anio, p.mes)}` : l;
            }} />} />
            <Area dataKey="total" name="Patrimonio" stroke={c.ingresos} strokeWidth={2} fill={c.ingresos} fillOpacity={0.15}
              dot={false} activeDot={{ r: 5, stroke: c.fondo, strokeWidth: 2 }} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      <p className="text-sm text-gray-600 dark:text-gray-300 mt-2">
        En 12 meses tu patrimonio ha {cambio >= 0 ? 'crecido' : 'bajado'}{' '}
        <span className={`font-semibold tabular-nums ${cambio >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>{formatoEuros(Math.abs(cambio))}</span>.
      </p>
      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
        Incluye todas las cuentas, también las de saldo oculto, y las tarjetas de crédito restan. Las inversiones valen lo de su última valoración más lo aportado después.
      </p>
      <TablaDatos
        cabecera={['Mes', 'Cuentas', 'Huchas', 'Inversiones', 'Total']}
        filas={serie.map(p => [mesCorto(p.anio, p.mes), formatoEuros(p.cuentas), formatoEuros(p.huchas), formatoEuros(p.inversiones), formatoEuros(p.total)])}
      />
    </Tarjeta>
  );
};

export const SeccionCuentas: React.FC<{ vista: VistaInforme }> = ({ vista }) => {
  const { esPlus } = usePerfil();
  // El patrimonio llega como mucho hasta el mes actual: los meses por venir no tienen datos.
  const hoy = new Date();
  const [a, m] = vista.mes === null ? [vista.anio, 12] : [vista.anio, vista.mes];
  const futuro = a > hoy.getFullYear() || (a === hoy.getFullYear() && m > hoy.getMonth() + 1);
  const [anioFin, mesFin] = futuro ? [hoy.getFullYear(), hoy.getMonth() + 1] : [a, m];
  return (
    <div className="space-y-4">
      {esPlus
        ? <Patrimonio anioFin={anioFin} mesFin={mesFin} />
        : <BloquePlus titulo="Patrimonio" texto="Todo lo que tienes sumado (cuentas, huchas e inversiones) y cómo ha cambiado mes a mes." />}
      <GastoPorCuenta vista={vista} />
      <Huchas />
    </div>
  );
};
